import jsPDF from "jspdf";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

import { formatMoney } from "./dtePrintFormatters";

pdfMake.addVirtualFileSystem(pdfFonts);

const PDF_TEXT_SIZE_SCALE = 0.92;

const COLORS = {
  brand: "#0f4c81",
  brandContrast: "#ffffff",
  surface: "#eef4fa",
  border: "#c7d5e5",
  text: "#1b2a3a",
  muted: "#5c6b7a",
  dangerBg: "#fbe1e1",
  dangerText: "#9b1c1c",
};

function safeText(value, fallback = "---") {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  return String(value);
}

function normalizePdfImage(image) {
  if (!image || typeof image !== "string") {
    return null;
  }

  if (image.startsWith("data:image/")) {
    return image;
  }

  return `data:image/png;base64,${image}`;
}

function buildCard(title, body, options = {}) {
  return {
    margin: options.margin || [0, 0, 0, 0],
    table: {
      widths: ["*"],
      body: [
        [{
          text: title,
          style: options.centerTitle ? ["cardTitle", "cardTitleCentered"] : "cardTitle",
          fillColor: COLORS.surface,
          border: [true, true, true, true],
        }],
        [{
          stack: Array.isArray(body) ? body : [body],
          border: [true, false, true, true],
        }],
      ],
    },
    layout: {
      hLineColor: () => COLORS.border,
      vLineColor: () => COLORS.border,
      hLineWidth: () => 1,
      vLineWidth: () => 1,
      paddingLeft: () => 8,
      paddingRight: () => 8,
      paddingTop: (index) => (index === 0 ? 5 : 8),
      paddingBottom: (index) => (index === 0 ? 5 : 8),
    },
  };
}

function buildKeyValueTable(rows = [], options = {}) {
  const body = rows
    .filter((row) => row && row.label && row.value)
    .map((row) => ([
      {
        text: row.label,
        bold: true,
        color: COLORS.muted,
        fontSize: options.fontSize || 7.6,
      },
      {
        text: safeText(row.value),
        color: COLORS.text,
        fontSize: options.fontSize || 7.6,
        alignment: row.alignment || "left",
      },
    ]));

  return {
    table: {
      widths: [options.labelWidth || 92, "*"],
      body,
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 1,
      paddingBottom: () => 2,
    },
  };
}

function buildBadgeContent(badges = []) {
  return badges.map((badge) => ({
    text: safeText(badge.label),
    color: badge.tone === "danger" ? COLORS.dangerText : COLORS.text,
    fillColor: badge.tone === "danger" ? COLORS.dangerBg : COLORS.surface,
    bold: true,
    fontSize: 8,
    margin: [0, 0, 0, 6],
    alignment: "center",
  }));
}

function buildIssuerCard(documentData) {
  const logo = normalizePdfImage(documentData.issuer.logoUrl);
  const issuerRows = documentData.issuer.billedInfoText
    ? [{
      text: documentData.issuer.billedInfoText,
      fontSize: 7.8,
      color: COLORS.text,
      lineHeight: 1.15,
      margin: [0, 0, 0, 0],
    }]
    : [
      buildKeyValueTable([
        { label: "Negocio", value: documentData.issuer.businessName },
        { label: "Razon social", value: documentData.issuer.tradeName },
        { label: "Actividad", value: documentData.issuer.activity },
        { label: "Direccion", value: documentData.issuer.address },
        {
          label: "Contacto",
          value: [documentData.issuer.phone, documentData.issuer.mobile, documentData.issuer.email].filter(Boolean).join(" | "),
        },
        { label: "NIT / NRC", value: `${safeText(documentData.issuer.nit)} / ${safeText(documentData.issuer.nrc)}` },
      ], { labelWidth: 78, fontSize: 7.5 }),
    ];

  const body = [];

  if (logo) {
    body.push({
      image: logo,
      fit: [180, 62],
      alignment: "left",
      margin: [0, 0, 0, 6],
    });
  }

  body.push(...issuerRows);
  return buildCard("Emisor", body);
}

function buildDocumentMetaCard(documentData) {
  return buildCard(
    documentData.meta.invoiceTypeLabel,
    buildKeyValueTable([
      { label: "Codigo generacion", value: documentData.meta.generationCode },
      { label: "Sello recepcion", value: documentData.meta.receiptSeal },
      { label: "Numero control", value: documentData.meta.controlNumber },
      { label: "Correlativo", value: documentData.meta.invoiceId },
      { label: "Fecha emision", value: documentData.meta.issueDate },
      { label: "Fecha vencimiento", value: documentData.meta.dueDate },
      { label: "Modelo", value: documentData.meta.modelLabel },
      { label: "Transmision", value: documentData.meta.transmissionLabel },
    ], { labelWidth: 96, fontSize: 7.1 }),
    { centerTitle: true }
  );
}

function buildBannerContent(documentData) {
  if (
    documentData.templateSettings &&
    documentData.templateSettings.theme &&
    (documentData.templateSettings.theme.hideAdvertising ||
      documentData.templateSettings.theme.bannerFullPage)
  ) {
    return null;
  }

  const banner = normalizePdfImage(documentData.issuer.bannerUrl);

  if (!banner) {
    return null;
  }

  return {
    image: banner,
    fit: [560, 124],
    alignment: "center",
    margin: [0, 8, 0, 0],
  };
}

function buildFullPageBannerContent(documentData, pageNumber, totalPages, layout) {
  if (
    !documentData.issuer.bannerUrl ||
    !documentData.templateSettings ||
    !documentData.templateSettings.theme ||
    documentData.templateSettings.theme.hideAdvertising ||
    !documentData.templateSettings.theme.bannerFullPage
  ) {
    return null;
  }

  const banner = normalizePdfImage(documentData.issuer.bannerUrl);
  if (!banner) {
    return null;
  }

  const pageLabel = layout === "standard"
    ? `Pagina ${pageNumber} de ${totalPages}`
    : `Pag. ${pageNumber} / ${totalPages}`;

  return {
    stack: [
      {
        image: banner,
        fit: [612, 792],
        absolutePosition: { x: 0, y: 0 },
      },
      {
        text: pageLabel,
        alignment: "right",
        fontSize: 7,
        color: COLORS.muted,
        margin: [0, 770, 14, 0],
      },
    ],
    pageBreak: "after",
  };
}

function buildRecipientSection(documentData) {
  const left = buildKeyValueTable([
    { label: "Codigo cliente", value: documentData.receiver.code },
    { label: "Razon social", value: documentData.receiver.name },
    { label: "Direccion", value: [documentData.receiver.address, documentData.receiver.addressSecondary].filter(Boolean).join("\n") },
    { label: "Actividad", value: documentData.receiver.activity },
    { label: "Entidad", value: documentData.receiver.entity },
    { label: "Zona", value: documentData.receiver.zone },
  ], { labelWidth: 82, fontSize: 7.5 });

  const right = buildKeyValueTable([
    ...(documentData.receiver.documentNumber
      ? [{ label: documentData.receiver.documentLabel, value: documentData.receiver.documentNumber }]
      : []),
    { label: "NIT", value: documentData.receiver.nit },
    { label: "NRC", value: documentData.receiver.nrc },
    { label: "Correo", value: documentData.receiver.email },
    { label: "Telefono", value: documentData.receiver.phone },
    {
      label: "Vendedor",
      value: [documentData.receiver.sellerCode, documentData.receiver.sellerName].filter(Boolean).join(" - "),
    },
    { label: "Contacto", value: documentData.receiver.contactName },
  ], { labelWidth: 72, fontSize: 7.5 });

  const body = [{
    columns: [
      { width: "*", stack: [left] },
      { width: 12, text: "" },
      { width: "*", stack: [right] },
    ],
    columnGap: 0,
  }];

  return buildCard("Receptor", body, { margin: [0, 8, 0, 0] });
}

function buildThirdPartySection(documentData) {
  if (!documentData.thirdPartySale) {
    return null;
  }

  return buildCard(
    "Venta por Cuenta de Terceros",
    buildKeyValueTable([
      { label: "NIT", value: documentData.thirdPartySale.nit },
      { label: "Nombre", value: documentData.thirdPartySale.name },
    ], { labelWidth: 72, fontSize: 7.5 }),
    { margin: [0, 8, 0, 0] }
  );
}

function buildRelatedDocumentsSection(title, documents = []) {
  if (!documents.length) {
    return null;
  }

  const body = [
    [
      { text: "Tipo", style: "tableHeader" },
      { text: "Numero", style: "tableHeader" },
      { text: "Fecha / Detalle", style: "tableHeader" },
    ],
    ...documents.map((document) => ([
      { text: safeText(document.typeLabel), style: "tableCell" },
      { text: safeText(document.number), style: "tableCell" },
      { text: safeText(document.date), style: "tableCell" },
    ])),
  ];

  return buildCard(title, {
    table: {
      headerRows: 1,
      widths: [110, 150, "*"],
      body,
    },
    layout: "lightHorizontalLines",
  }, { margin: [0, 8, 0, 0] });
}

function showCodeColumn(documentData) {
  return documentData.items.some((item) => item.code);
}

function showBarcodeColumn(documentData) {
  return documentData.items.some((item) => item.barcode);
}

function showDiscountColumn(documentData) {
  return documentData.meta.invoiceType !== "CDD";
}

function showExemptColumn(documentData) {
  return !["FEX", "CDD"].includes(documentData.meta.invoiceType);
}

function showNonSubjectColumn(documentData) {
  return !["FEX", "CDD"].includes(documentData.meta.invoiceType);
}

function showNonTaxedColumn(documentData) {
  return ["FCF", "CCF", "FEX"].includes(documentData.meta.invoiceType);
}

function getMainAmountLabel(documentData) {
  if (documentData.meta.invoiceType === "FEX") {
    return "Afecta";
  }

  if (documentData.meta.invoiceType === "CDD") {
    return "Donado";
  }

  return "Gravada";
}

function getMainAmountValue(documentData, item) {
  if (documentData.meta.invoiceType === "CDD") {
    return item.total;
  }

  if (documentData.meta.invoiceType === "FEX") {
    return item.taxed || item.total;
  }

  return item.taxed;
}

function buildCompactDetailColumns(documentData) {
  return [
    { key: "index", label: "Item", width: 22, align: "right", value: (item) => safeText(item.index) },
    { key: "description", label: "Descripcion", width: "*", value: (item) => safeText(item.description) },
    { key: "quantity", label: "Cantidad", width: 42, align: "right", value: (item) => safeText(item.quantity) },
    {
      key: "total",
      label: "Total",
      width: 56,
      align: "right",
      value: (item) => formatMoney(item.total, documentData.meta.currency),
    },
  ];
}

function buildFullDetailColumns(documentData) {
  const columns = [
    { key: "index", label: "Item", width: 18, align: "right", value: (item) => safeText(item.index) },
    { key: "quantity", label: "Cant.", width: 28, align: "right", value: (item) => safeText(item.quantity) },
  ];

  if (showCodeColumn(documentData)) {
    columns.push({ key: "code", label: "Codigo", width: 34, value: (item) => safeText(item.code) });
  }

  if (showBarcodeColumn(documentData)) {
    columns.push({ key: "barcode", label: "Cod. alterno", width: 40, value: (item) => safeText(item.barcode) });
  }

  columns.push({ key: "description", label: "Descripcion", width: "*", value: (item) => safeText(item.description) });
  columns.push({
    key: "unitPrice",
    label: "P. Unitario",
    width: 40,
    align: "right",
    value: (item) => formatMoney(item.unitPrice, documentData.meta.currency),
  });

  if (showDiscountColumn(documentData)) {
    columns.push({
      key: "discount",
      label: "Desc.",
      width: 34,
      align: "right",
      value: (item) => formatMoney(item.discount, documentData.meta.currency),
    });
  }

  if (showExemptColumn(documentData)) {
    columns.push({
      key: "exempt",
      label: "Exenta",
      width: 34,
      align: "right",
      value: (item) => formatMoney(item.exempt, documentData.meta.currency),
    });
  }

  if (showNonSubjectColumn(documentData)) {
    columns.push({
      key: "nonSubject",
      label: "No sujeta",
      width: 36,
      align: "right",
      value: (item) => formatMoney(item.nonSubject, documentData.meta.currency),
    });
  }

  if (showNonTaxedColumn(documentData)) {
    columns.push({
      key: "nonTaxed",
      label: "No afecta",
      width: 36,
      align: "right",
      value: (item) => formatMoney(item.nonTaxed, documentData.meta.currency),
    });
  }

  columns.push({
    key: "mainAmount",
    label: getMainAmountLabel(documentData),
    width: 38,
    align: "right",
    value: (item) => formatMoney(getMainAmountValue(documentData, item), documentData.meta.currency),
  });

  return columns;
}

function buildDetailSection(documentData, page, layout) {
  const columns = layout === "compact"
    ? buildCompactDetailColumns(documentData)
    : buildFullDetailColumns(documentData);
  const body = [
    columns.map((column) => ({
      text: column.label,
      style: "detailTableHeader",
      alignment: column.align || "left",
    })),
  ];

  if (page.items.length) {
    page.items.forEach((item) => {
      body.push(columns.map((column) => ({
        text: column.value(item),
        style: "detailTableCell",
        alignment: column.align || "left",
        noWrap: column.key !== "description",
      })));
    });
  } else {
    body.push([
      {
        text: "Documento sin lineas de detalle",
        colSpan: columns.length,
        style: "detailTableCell",
        alignment: "center",
        color: COLORS.muted,
      },
      ...columns.slice(1).map(() => ({})),
    ]);
  }

  return {
    margin: [0, 8, 0, 0],
    table: {
      widths: ["*"],
      body: [[{
        table: {
          headerRows: 1,
          widths: columns.map((column) => column.width),
          body,
        },
        layout: "lightHorizontalLines",
        border: [true, true, true, true],
      }]],
    },
    layout: {
      hLineColor: () => COLORS.border,
      vLineColor: () => COLORS.border,
      hLineWidth: () => 1,
      vLineWidth: () => 1,
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
  };
}

function buildTotalsSection(documentData) {
  const totalsRows = [];

  totalsRows.push({
    columns: [
      {
        width: "*",
        text: `Estado: ${safeText(documentData.meta.paymentStatus)}    Moneda: ${safeText(documentData.meta.currency)}`,
        fontSize: 7.3,
        bold: true,
        color: COLORS.text,
      },
    ],
    margin: [0, 0, 0, 4],
  });

  if (documentData.amountInWords) {
    totalsRows.push({
      table: {
        widths: ["*", 160],
        body: [[
          { text: "Valor en letras", bold: true, fontSize: 7.4, color: COLORS.text, border: [false, false, false, true] },
          { text: safeText(documentData.amountInWords), fontSize: 7.4, color: COLORS.text, border: [false, false, false, true], alignment: "right" },
        ]],
      },
      layout: {
        hLineColor: () => COLORS.border,
        vLineWidth: () => 0,
        hLineWidth: () => 0.5,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 1,
        paddingBottom: () => 4,
      },
    });
  }

  totalsRows.push({
    table: {
      widths: ["*", 110],
      body: documentData.totalRows.map((row) => ([
        {
          text: safeText(row.label),
          fontSize: row.emphasis ? 8.2 : 7.5,
          bold: Boolean(row.emphasis),
          color: row.emphasis ? COLORS.brand : COLORS.text,
          border: [false, false, false, true],
        },
        {
          text: safeText(row.value),
          fontSize: row.emphasis ? 8.2 : 7.5,
          bold: Boolean(row.emphasis),
          color: row.emphasis ? COLORS.brand : COLORS.text,
          border: [false, false, false, true],
          alignment: "right",
        },
      ])),
    },
    layout: {
      hLineColor: () => COLORS.border,
      vLineWidth: () => 0,
      hLineWidth: () => 0.5,
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 2,
      paddingBottom: () => 4,
    },
  });

  return buildCard("Totales", {
    stack: totalsRows,
  }, { margin: [0, 8, 0, 0] });
}

function buildQrSection(documentData) {
  const qrImage = normalizePdfImage(documentData.qrImage);

  if (!documentData.showQrSection || !qrImage) {
    return null;
  }

  return buildCard("Consulta publica", [
    {
      text: "Escanea este codigo QR para verificar la validez del comprobante en la consulta publica de Hacienda.",
      fontSize: 7.2,
      color: COLORS.muted,
      alignment: "center",
      lineHeight: 1.18,
      margin: [0, 0, 0, 8],
    },
    {
      image: qrImage,
      fit: [88, 88],
      alignment: "center",
    },
  ], { margin: [0, 8, 0, 0] });
}

function buildFooterSection(documentData) {
  const content = [];

  if (documentData.notes) {
    content.push({
      text: "Observaciones",
      style: "sectionSubtitle",
      margin: [0, 0, 0, 4],
    });
    content.push({
      text: safeText(documentData.notes),
      fontSize: 7.6,
      color: COLORS.text,
      lineHeight: 1.18,
      margin: [0, 0, 0, 8],
    });
  }

  if (documentData.showLargeAmountResponsibles) {
    content.push({
      columns: [
        {
          width: "*",
          stack: [
            { text: "Responsable Emisor", style: "sectionSubtitle", margin: [0, 0, 0, 4] },
            buildKeyValueTable([
              { label: "Nombre", value: documentData.footerContacts.issuerName },
              { label: "Documento", value: documentData.footerContacts.issuerDocument },
              { label: "Telefono", value: documentData.footerContacts.issuerPhone },
            ], { labelWidth: 62, fontSize: 7.4 }),
          ],
        },
        {
          width: 14,
          text: "",
        },
        {
          width: "*",
          stack: [
            { text: "Responsable Receptor", style: "sectionSubtitle", margin: [0, 0, 0, 4] },
            buildKeyValueTable([
              { label: "Nombre", value: documentData.footerContacts.receiverName },
              { label: "Documento", value: documentData.footerContacts.receiverDocument },
              { label: "Telefono", value: documentData.footerContacts.receiverPhone },
            ], { labelWidth: 62, fontSize: 7.4 }),
          ],
        },
      ],
      columnGap: 0,
    });
  }

  if (!content.length) {
    return null;
  }

  return buildCard("Notas y Cierre", content, { margin: [0, 8, 0, 0] });
}

function buildPageContent(documentData, page, pages, layout, options = {}) {
  const content = [];
  const pageOffset = options.pageOffset || 0;
  const totalPages = options.totalPages || pages.length;
  const pageNumber = page.number + pageOffset;

  content.push(...buildBadgeContent(documentData.badges));
  content.push({
    columns: [
      { width: "52%", stack: [buildIssuerCard(documentData)] },
      { width: "48%", stack: [buildDocumentMetaCard(documentData)] },
    ],
    columnGap: 10,
  });

  const banner = buildBannerContent(documentData);
  if (banner) {
    content.push(banner);
  }

  if (page.showReceiver) {
    content.push(buildRecipientSection(documentData));
  }

  const thirdPartySection = buildThirdPartySection(documentData);
  if (page.showReceiver && thirdPartySection) {
    content.push(thirdPartySection);
  }

  if (page.showRelated) {
    content.push(buildRelatedDocumentsSection("Documentos Relacionados", documentData.relatedDocuments));
  }

  if (page.showAssociated) {
    content.push(buildRelatedDocumentsSection("Documentos Asociados", documentData.associatedDocuments));
  }

  content.push(buildDetailSection(documentData, page, layout));

  if (page.showTail) {
    const qrSection = buildQrSection(documentData);

    if (qrSection) {
      content.push({
        columns: [
          { width: "66.6667%", stack: [buildTotalsSection(documentData)] },
          { width: "33.3333%", stack: [qrSection] },
        ],
        columnGap: 10,
      });
    } else {
      content.push(buildTotalsSection(documentData));
    }

    const footer = buildFooterSection(documentData);
    if (footer) {
      content.push(footer);
    }
  }

  content.push({
    text: layout === "standard"
      ? `Pagina ${pageNumber} de ${totalPages}`
      : `Pag. ${pageNumber} / ${totalPages}`,
    alignment: "right",
    fontSize: 7,
    color: COLORS.muted,
    margin: [0, 8, 0, 0],
  });

  return {
    stack: content.filter(Boolean),
    pageBreak: page.number < pages.length ? "after" : undefined,
  };
}

export async function buildPdfArrayBuffer({ documentData, pages = [], layout = "full" } = {}) {
  const safePages = (pages || []).filter(Boolean);
  const fullPageBanner = buildFullPageBannerContent(
    documentData,
    1,
    safePages.length + 1,
    layout
  );
  const pageOffset = fullPageBanner ? 1 : 0;
  const totalPages = safePages.length + pageOffset;

  const docDefinition = {
    pageSize: "LETTER",
    pageMargins: [24, 24, 24, 20],
    content: [
      ...(fullPageBanner ? [fullPageBanner] : []),
      ...safePages.map((page) => buildPageContent(documentData, page, safePages, layout, {
        pageOffset,
        totalPages,
      })),
    ],
    defaultStyle: {
      fontSize: 8,
      color: COLORS.text,
    },
    styles: {
      cardTitle: {
        fontSize: 7,
        bold: true,
        color: COLORS.muted,
        alignment: "left",
      },
      cardTitleCentered: {
        alignment: "center",
      },
      sectionSubtitle: {
        fontSize: 8,
        bold: true,
        color: COLORS.brand,
      },
      tableHeader: {
        fontSize: layout === "compact" ? 6.8 : 6.4,
        bold: true,
        color: COLORS.brandContrast,
        fillColor: COLORS.brand,
        margin: [0, 2, 0, 2],
      },
      tableCell: {
        fontSize: layout === "compact" ? 7 : 6.6,
        color: COLORS.text,
        margin: [0, 1, 0, 1],
      },
      detailTableHeader: {
        fontSize: layout === "compact" ? 6.1 : 5.8,
        bold: true,
        color: COLORS.brandContrast,
        fillColor: COLORS.brand,
        margin: [0, 1, 0, 1],
      },
      detailTableCell: {
        fontSize: layout === "compact" ? 6.4 : 6.05,
        color: COLORS.text,
        margin: [0, 0.5, 0, 0.5],
      },
    },
  };

  return new Promise((resolve, reject) => {
    try {
      pdfMake.createPdf(docDefinition).getBuffer((buffer) => {
        const arrayBuffer = buffer.buffer.slice(
          buffer.byteOffset,
          buffer.byteOffset + buffer.byteLength
        );
        resolve(arrayBuffer);
      });
    } catch (error) {
      reject(error);
    }
  });
}

function parsePdfColor(color) {
  if (!color || color === "transparent") {
    return null;
  }

  const rgbaMatch = String(color)
    .trim()
    .match(/^rgba?\(([^)]+)\)$/i);

  if (rgbaMatch) {
    const channels = rgbaMatch[1]
      .split(",")
      .map((value) => value.trim());
    const alpha = channels.length > 3 ? Number(channels[3]) : 1;

    if (!Number.isNaN(alpha) && alpha <= 0) {
      return null;
    }

    const rgb = channels.slice(0, 3).map((value) => {
      const numeric = Number.parseFloat(value);
      if (Number.isNaN(numeric)) {
        return 0;
      }
      return Math.max(0, Math.min(255, Math.round(numeric)));
    });

    if (!Number.isNaN(alpha) && alpha < 1) {
      return rgb.map((value) => Math.round((value * alpha) + (255 * (1 - alpha))));
    }

    return rgb;
  }

  const hexMatch = String(color).trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!hexMatch) {
    return null;
  }

  const hex = hexMatch[1].length === 3
    ? hexMatch[1].split("").map((value) => `${value}${value}`).join("")
    : hexMatch[1];

  return [
    Number.parseInt(hex.slice(0, 2), 16),
    Number.parseInt(hex.slice(2, 4), 16),
    Number.parseInt(hex.slice(4, 6), 16),
  ];
}

function setPdfColor(doc, method, color) {
  const parsed = parsePdfColor(color);
  if (!parsed) {
    return false;
  }

  doc[method](parsed[0], parsed[1], parsed[2]);
  return true;
}

function resolveBackgroundColor(style) {
  if (parsePdfColor(style.backgroundColor)) {
    return style.backgroundColor;
  }

  const imageColorMatch = String(style.backgroundImage || "").match(/rgba?\([^)]+\)/i);
  return imageColorMatch ? imageColorMatch[0] : "";
}

function isRenderableElement(element) {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }

  const style = element.ownerDocument.defaultView.getComputedStyle(element);
  return (
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    Number.parseFloat(style.opacity || "1") > 0
  );
}

function getElementPdfRect(element, pageRect, scale) {
  const rect = element.getBoundingClientRect();

  return {
    x: (rect.left - pageRect.left) * scale,
    y: (rect.top - pageRect.top) * scale,
    width: rect.width * scale,
    height: rect.height * scale,
  };
}

function getStyleNumber(styleValue, fallback = 0) {
  const value = Number.parseFloat(styleValue || "");
  return Number.isNaN(value) ? fallback : value;
}

function getElementContentPdfRect(element, pageRect, scale) {
  const style = element.ownerDocument.defaultView.getComputedStyle(element);
  const rect = getElementPdfRect(element, pageRect, scale);
  const paddingLeft = getStyleNumber(style.paddingLeft) * scale;
  const paddingRight = getStyleNumber(style.paddingRight) * scale;
  const paddingTop = getStyleNumber(style.paddingTop) * scale;
  const paddingBottom = getStyleNumber(style.paddingBottom) * scale;

  return {
    x: rect.x + paddingLeft,
    y: rect.y + paddingTop,
    width: Math.max(0, rect.width - paddingLeft - paddingRight),
    height: Math.max(0, rect.height - paddingTop - paddingBottom),
  };
}

function getElementBorderRadius(style, scale, rect) {
  const radius = Math.max(
    getStyleNumber(style.borderTopLeftRadius),
    getStyleNumber(style.borderTopRightRadius),
    getStyleNumber(style.borderBottomRightRadius),
    getStyleNumber(style.borderBottomLeftRadius)
  ) * scale;

  return Math.max(0, Math.min(radius, rect.width / 2, rect.height / 2));
}

function drawElementBackground(doc, element, pageRect, scale) {
  const style = element.ownerDocument.defaultView.getComputedStyle(element);
  const rect = getElementPdfRect(element, pageRect, scale);
  const radius = getElementBorderRadius(style, scale, rect);

  if (rect.width <= 0 || rect.height <= 0) {
    return;
  }

  if (setPdfColor(doc, "setFillColor", resolveBackgroundColor(style))) {
    if (radius > 0) {
      doc.roundedRect(rect.x, rect.y, rect.width, rect.height, radius, radius, "F");
    } else {
      doc.rect(rect.x, rect.y, rect.width, rect.height, "F");
    }
  }
}

function drawBorderLine(doc, color, width, x1, y1, x2, y2) {
  const numericWidth = Number.parseFloat(width);

  if (!numericWidth || numericWidth <= 0) {
    return;
  }

  if (!setPdfColor(doc, "setDrawColor", color)) {
    return;
  }

  doc.setLineWidth(numericWidth);
  doc.line(x1, y1, x2, y2);
}

function drawElementBorders(doc, element, pageRect, scale) {
  const style = element.ownerDocument.defaultView.getComputedStyle(element);
  const rect = getElementPdfRect(element, pageRect, scale);
  const borderColor = style.borderTopColor;
  const borderWidth = getStyleNumber(style.borderTopWidth) * scale;
  const radius = getElementBorderRadius(style, scale, rect);
  const hasUniformBorder =
    borderWidth > 0 &&
    style.borderRightColor === borderColor &&
    style.borderBottomColor === borderColor &&
    style.borderLeftColor === borderColor &&
    getStyleNumber(style.borderRightWidth) === getStyleNumber(style.borderTopWidth) &&
    getStyleNumber(style.borderBottomWidth) === getStyleNumber(style.borderTopWidth) &&
    getStyleNumber(style.borderLeftWidth) === getStyleNumber(style.borderTopWidth);

  if (rect.width <= 0 || rect.height <= 0) {
    return;
  }

  if (hasUniformBorder && radius > 0 && setPdfColor(doc, "setDrawColor", borderColor)) {
    doc.setLineWidth(borderWidth);
    doc.roundedRect(rect.x, rect.y, rect.width, rect.height, radius, radius, "S");
    return;
  }

  drawBorderLine(
    doc,
    style.borderTopColor,
    getStyleNumber(style.borderTopWidth) * scale,
    rect.x,
    rect.y,
    rect.x + rect.width,
    rect.y
  );
  drawBorderLine(
    doc,
    style.borderRightColor,
    getStyleNumber(style.borderRightWidth) * scale,
    rect.x + rect.width,
    rect.y,
    rect.x + rect.width,
    rect.y + rect.height
  );
  drawBorderLine(
    doc,
    style.borderBottomColor,
    getStyleNumber(style.borderBottomWidth) * scale,
    rect.x,
    rect.y + rect.height,
    rect.x + rect.width,
    rect.y + rect.height
  );
  drawBorderLine(
    doc,
    style.borderLeftColor,
    getStyleNumber(style.borderLeftWidth) * scale,
    rect.x,
    rect.y,
    rect.x,
    rect.y + rect.height
  );
}

function beginElementClip(doc, element, pageRect, scale) {
  const style = element.ownerDocument.defaultView.getComputedStyle(element);
  const rect = getElementPdfRect(element, pageRect, scale);
  const radius = getElementBorderRadius(style, scale, rect);
  const clipsChildren = (
    radius > 0 &&
    (style.overflow === "hidden" ||
      style.overflowX === "hidden" ||
      style.overflowY === "hidden")
  );

  if (!clipsChildren || rect.width <= 0 || rect.height <= 0) {
    return false;
  }

  doc.saveGraphicsState();
  doc.roundedRect(rect.x, rect.y, rect.width, rect.height, radius, radius, null);
  doc.clip();
  doc.discardPath();
  return true;
}

function getImageFormat(source) {
  const normalized = String(source || "").toLowerCase();

  if (normalized.startsWith("data:image/jpeg") || normalized.startsWith("data:image/jpg")) {
    return "JPEG";
  }

  if (normalized.startsWith("data:image/webp")) {
    return "WEBP";
  }

  return "PNG";
}

function drawImageElement(doc, element, pageRect, scale) {
  const source = element.getAttribute("src");
  const rect = getElementPdfRect(element, pageRect, scale);

  if (!source || rect.width <= 0 || rect.height <= 0) {
    return;
  }

  try {
    doc.addImage(
      source,
      getImageFormat(source),
      rect.x,
      rect.y,
      rect.width,
      rect.height
    );
  } catch (error) {
    // Las imagenes externas no deben bloquear la generacion del texto PDF.
  }
}

function getPdfFontStyle(style) {
  const weight = Number.parseInt(style.fontWeight, 10);
  const isBold = style.fontWeight === "bold" || (!Number.isNaN(weight) && weight >= 600);
  const isItalic = style.fontStyle === "italic" || style.fontStyle === "oblique";

  if (isBold && isItalic) {
    return "bolditalic";
  }

  if (isBold) {
    return "bold";
  }

  if (isItalic) {
    return "italic";
  }

  return "normal";
}

function normalizePdfText(text, style) {
  const value = String(text || "");
  let normalized = "";

  if (style.whiteSpace === "pre" || style.whiteSpace === "pre-wrap") {
    normalized = value.replace(/[ \t\f\v]+/g, " ");
  } else if (style.whiteSpace === "pre-line") {
    normalized = value
      .split(/\n+/)
      .map((line) => line.replace(/[ \t\f\v]+/g, " ").trim())
      .filter(Boolean)
      .join("\n");
  } else {
    normalized = value.replace(/\s+/g, " ").trim();
  }

  if (style.textTransform === "uppercase") {
    return normalized.toUpperCase();
  }

  if (style.textTransform === "lowercase") {
    return normalized.toLowerCase();
  }

  return normalized;
}

function getTextAlign(style) {
  if (style.textAlign === "right" || style.textAlign === "center") {
    return style.textAlign;
  }

  return "left";
}

function getTextX(contentRect, align) {
  if (align === "right") {
    return contentRect.x + contentRect.width;
  }

  if (align === "center") {
    return contentRect.x + (contentRect.width / 2);
  }

  return contentRect.x;
}

function getTextMaxWidth(parent, contentRect, pageRect, scale) {
  const totalRow = parent.closest && parent.closest(".dte-total-row");

  if (totalRow && parent === totalRow.lastElementChild) {
    const rowRect = getElementContentPdfRect(totalRow, pageRect, scale);
    return Math.max(contentRect.width, (rowRect.x + rowRect.width) - contentRect.x);
  }

  return contentRect.width;
}

function splitLongPdfWord(doc, word, maxWidth) {
  const chunks = [];
  let pending = word;

  while (pending && doc.getTextWidth(pending) > maxWidth) {
    let length = pending.length;

    while (length > 1 && doc.getTextWidth(pending.slice(0, length)) > maxWidth) {
      length -= 1;
    }

    chunks.push(pending.slice(0, length));
    pending = pending.slice(length);
  }

  if (pending) {
    chunks.push(pending);
  }

  return chunks;
}

function splitPdfLineToSize(doc, line, maxWidth) {
  const words = String(line || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    const wordParts = splitLongPdfWord(doc, word, maxWidth);

    wordParts.forEach((part) => {
      const candidate = currentLine ? `${currentLine} ${part}` : part;

      if (!currentLine || doc.getTextWidth(candidate) <= maxWidth) {
        currentLine = candidate;
        return;
      }

      lines.push(currentLine);
      currentLine = part;
    });
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.length ? lines : [""];
}

function splitPdfTextToSize(doc, text, maxWidth) {
  return String(text || "")
    .split("\n")
    .reduce((lines, line) => lines.concat(splitPdfLineToSize(doc, line, Math.max(8, maxWidth))), []);
}

function drawTextNode(doc, textNode, pageRect, scale) {
  const parent = textNode.parentElement;

  if (!parent || !isRenderableElement(parent)) {
    return;
  }

  const ownerDocument = parent.ownerDocument;
  const style = ownerDocument.defaultView.getComputedStyle(parent);
  const text = normalizePdfText(textNode.nodeValue, style);

  if (!text) {
    return;
  }

  const range = ownerDocument.createRange();
  range.selectNodeContents(textNode);

  const rect = range.getBoundingClientRect();
  if (typeof range.detach === "function") {
    range.detach();
  }

  if (!rect.height) {
    return;
  }

  const contentRect = getElementContentPdfRect(parent, pageRect, scale);
  const fontSizePx = getStyleNumber(style.fontSize, 10);
  const lineHeightPx = style.lineHeight === "normal"
    ? fontSizePx * 1.18
    : getStyleNumber(style.lineHeight, fontSizePx * 1.18);
  const fontSize = fontSizePx * scale * PDF_TEXT_SIZE_SCALE;
  const lineHeight = lineHeightPx * scale * PDF_TEXT_SIZE_SCALE;
  const align = getTextAlign(style);
  const maxWidth = Math.max(8, getTextMaxWidth(parent, contentRect, pageRect, scale));
  const x = getTextX(contentRect, align);
  const rangeY = (rect.top - pageRect.top) * scale;
  const y = Math.max(rangeY, contentRect.y) + fontSize * 0.9;

  doc.setFont("helvetica", getPdfFontStyle(style));
  doc.setFontSize(fontSize);
  const lines = splitPdfTextToSize(doc, text, maxWidth);

  if (!setPdfColor(doc, "setTextColor", style.color)) {
    doc.setTextColor(0, 0, 0);
  }

  doc.text(lines, x, y, {
    align,
    baseline: "alphabetic",
    lineHeightFactor: lineHeight / fontSize,
    maxWidth,
  });
}

function getGuaranteeTextStyleFlags(element, inherited = {}) {
  const tagName = String(element && element.tagName ? element.tagName : "").toLowerCase();

  return {
    bold: Boolean(inherited.bold || tagName === "strong" || tagName === "b"),
    italic: Boolean(inherited.italic || tagName === "em" || tagName === "i"),
  };
}

function getGuaranteePdfFontStyle(styleFlags) {
  if (styleFlags.bold && styleFlags.italic) {
    return "bolditalic";
  }

  if (styleFlags.bold) {
    return "bold";
  }

  if (styleFlags.italic) {
    return "italic";
  }

  return "normal";
}

function collectGuaranteeInlineRuns(node, inherited = {}) {
  const runs = [];
  const children = Array.from(node && node.childNodes ? node.childNodes : []);

  children.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = String(child.nodeValue || "").replace(/\s+/g, " ");
      if (text) {
        runs.push({
          text,
          bold: Boolean(inherited.bold),
          italic: Boolean(inherited.italic),
        });
      }
      return;
    }

    if (child.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const tagName = String(child.tagName || "").toLowerCase();

    if (tagName === "br") {
      runs.push({
        text: "\n",
        bold: Boolean(inherited.bold),
        italic: Boolean(inherited.italic),
      });
      return;
    }

    if (tagName === "ul" || tagName === "ol" || tagName === "p" || tagName === "div" || tagName === "li") {
      runs.push(...collectGuaranteeInlineRuns(child, getGuaranteeTextStyleFlags(child, inherited)));
      return;
    }

    runs.push(...collectGuaranteeInlineRuns(child, getGuaranteeTextStyleFlags(child, inherited)));
  });

  return runs;
}

function collectGuaranteeBlocks(root) {
  const blocks = [];
  const children = Array.from(root && root.childNodes ? root.childNodes : []);

  children.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = String(child.nodeValue || "").replace(/\s+/g, " ");
      if (text) {
        blocks.push({
          type: "paragraph",
          runs: [{
            text,
            bold: false,
            italic: false,
          }],
        });
      }
      return;
    }

    if (child.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const tagName = String(child.tagName || "").toLowerCase();

    if (tagName === "br") {
      blocks.push({ type: "spacer" });
      return;
    }

    if (tagName === "ul" || tagName === "ol") {
      Array.from(child.children || []).forEach((li, index) => {
        if (String(li.tagName || "").toLowerCase() !== "li") {
          return;
        }

        blocks.push({
          type: "listItem",
          ordered: tagName === "ol",
          index: index + 1,
          runs: collectGuaranteeInlineRuns(li, { bold: false, italic: false }),
        });
      });
      return;
    }

    if (tagName === "p" || tagName === "div" || tagName === "li") {
      const runs = collectGuaranteeInlineRuns(child, { bold: false, italic: false });
      if (runs.length) {
        blocks.push({
          type: tagName === "li" ? "listItem" : "paragraph",
          ordered: false,
          index: 1,
          runs,
        });
      }
      return;
    }

    const runs = collectGuaranteeInlineRuns(child, { bold: false, italic: false });
    if (runs.length) {
      blocks.push({
        type: "paragraph",
        runs,
      });
    }
  });

  return blocks;
}

function renderGuaranteeRuns(doc, runs, options) {
  const {
    x,
    y,
    maxWidth,
    fontSize,
    lineHeight,
    baseColor,
    clipBottom,
    indent = 0,
    prefix = "",
  } = options;
  const startX = x + indent;
  let cursorX = startX;
  let cursorY = y;

  if (prefix) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(fontSize);
    if (!setPdfColor(doc, "setTextColor", baseColor)) {
      doc.setTextColor(0, 0, 0);
    }
  }

  const prefixWidth = prefix ? doc.getTextWidth(prefix) : 0;
  const textStartX = prefix ? startX + prefixWidth + 4 : startX;

  if (prefix) {
    doc.text(prefix, startX, cursorY, { baseline: "alphabetic" });
    cursorX = textStartX;
  }

  runs.forEach((run) => {
    const text = String(run && run.text ? run.text : "");
    const fontStyle = getGuaranteePdfFontStyle(run || {});
    const parts = text.split(/(\n|\s+)/).filter((part) => part !== "");

    doc.setFont("helvetica", fontStyle);
    doc.setFontSize(fontSize);
    if (!setPdfColor(doc, "setTextColor", baseColor)) {
      doc.setTextColor(0, 0, 0);
    }

    parts.forEach((part) => {
      if (part === "\n") {
        cursorY += lineHeight;
        cursorX = textStartX;
        return;
      }

      const isWhitespace = /^\s+$/.test(part);
      if (isWhitespace) {
        if (cursorX === textStartX) {
          return;
        }
      }

      const partWidth = doc.getTextWidth(part);
      if (!isWhitespace && cursorX > textStartX && (cursorX + partWidth) > (startX + maxWidth)) {
        cursorY += lineHeight;
        cursorX = textStartX;
      }

      if (clipBottom && cursorY > clipBottom) {
        return;
      }

      if (isWhitespace && cursorX === textStartX) {
        return;
      }

      if (!isWhitespace && partWidth > maxWidth) {
        splitLongPdfWord(doc, part, maxWidth).forEach((chunk) => {
          const chunkWidth = doc.getTextWidth(chunk);
          if (cursorX > textStartX && (cursorX + chunkWidth) > (startX + maxWidth)) {
            cursorY += lineHeight;
            cursorX = textStartX;
          }

          if (clipBottom && cursorY > clipBottom) {
            return;
          }

          doc.text(chunk, cursorX, cursorY, { baseline: "alphabetic" });
          cursorX += chunkWidth;
        });
        return;
      }

      doc.text(part, cursorX, cursorY, { baseline: "alphabetic" });
      cursorX += partWidth;
    });
  });

  return cursorY + lineHeight;
}

function drawGuaranteeBlockText(doc, element, pageRect, scale) {
  const contentElement = element.querySelector(".guarantee-content") || element;
  const style = element.ownerDocument.defaultView.getComputedStyle(element);
  const contentRect = getElementContentPdfRect(element, pageRect, scale);
  const blocks = collectGuaranteeBlocks(contentElement);

  if (!blocks.length || contentRect.width <= 0 || contentRect.height <= 0) {
    return;
  }

  const fontSizePx = getStyleNumber(style.fontSize, 10);
  const lineHeightPx = style.lineHeight === "normal"
    ? fontSizePx * 1.5
    : getStyleNumber(style.lineHeight, fontSizePx * 1.5);
  const fontSize = fontSizePx * scale * PDF_TEXT_SIZE_SCALE;
  const lineHeight = lineHeightPx * scale * PDF_TEXT_SIZE_SCALE;
  const align = getTextAlign(style);
  const maxWidth = Math.max(8, contentRect.width);
  const x = getTextX(contentRect, align);
  let y = contentRect.y + (fontSize * 0.9);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);

  if (!setPdfColor(doc, "setTextColor", style.color)) {
    doc.setTextColor(0, 0, 0);
  }

  let cursorY = y;
  const clipBottom = contentRect.y + contentRect.height;

  blocks.forEach((block) => {
    if (cursorY > clipBottom) {
      return;
    }

    if (block.type === "spacer") {
      cursorY += lineHeight * 0.6;
      return;
    }

    if (block.type === "listItem") {
      const prefix = block.ordered ? `${block.index}. ` : "• ";
      cursorY = renderGuaranteeRuns(doc, block.runs, {
        x,
        y: cursorY,
        maxWidth,
        fontSize,
        lineHeight,
        baseColor: style.color,
        clipBottom,
        indent: 0,
        prefix,
      });
      cursorY += lineHeight * 0.15;
      return;
    }

    cursorY = renderGuaranteeRuns(doc, block.runs, {
      x,
      y: cursorY,
      maxWidth,
      fontSize,
      lineHeight,
      baseColor: style.color,
      clipBottom,
    });
    cursorY += lineHeight * 0.2;
  });
}

function drawDomElement(doc, element, pageRect, scale) {
  if (!isRenderableElement(element)) {
    return;
  }

  drawElementBackground(doc, element, pageRect, scale);

  if (element.classList && element.classList.contains("guarantee-print-block")) {
    drawGuaranteeBlockText(doc, element, pageRect, scale);
    return;
  }

  if (element.tagName === "IMG") {
    drawImageElement(doc, element, pageRect, scale);
  }

  const clipped = beginElementClip(doc, element, pageRect, scale);

  Array.from(element.childNodes || []).forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      drawTextNode(doc, child, pageRect, scale);
      return;
    }

    if (child.nodeType === Node.ELEMENT_NODE) {
      drawDomElement(doc, child, pageRect, scale);
    }
  });

  if (clipped) {
    doc.restoreGraphicsState();
  }

  drawElementBorders(doc, element, pageRect, scale);
}

function drawPreviewPage(doc, element) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageRect = element.getBoundingClientRect();
  const scale = pageRect.width > 0 ? pageWidth / pageRect.width : 0.75;

  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageWidth, pageHeight, "F");
  drawDomElement(doc, element, pageRect, scale);
}

export async function buildPreviewPdfArrayBuffer(elements = []) {
  const safeElements = (elements || []).filter(Boolean);
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "letter",
  });

  for (let index = 0; index < safeElements.length; index += 1) {
    if (index > 0) {
      doc.addPage("letter", "portrait");
    }

    drawPreviewPage(doc, safeElements[index]);
  }

  return doc.output("arraybuffer");
}
